import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, ClipboardList, CheckCircle, XCircle, Clock, Play, RotateCcw, MoreHorizontal, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

interface ChangeRequest {
  id: string
  title: string
  description: string
  type: string
  status: string
  risk: string
  scheduledStart: string | null
  scheduledEnd: string | null
  requestedBy: string
  tags: string[]
  createdAt: string
}

function statusBadge(status: string) {
  const map: Record<string, { variant: string; label: string }> = {
    draft: { variant: 'secondary', label: 'Draft' },
    submitted: { variant: 'outline', label: 'Submitted' },
    pending_approval: { variant: 'outline', label: 'Pending Approval' },
    approved: { variant: 'default', label: 'Approved' },
    rejected: { variant: 'destructive', label: 'Rejected' },
    in_progress: { variant: 'default', label: 'In Progress' },
    completed: { variant: 'default', label: 'Completed' },
    rolled_back: { variant: 'destructive', label: 'Rolled Back' },
    cancelled: { variant: 'secondary', label: 'Cancelled' },
  }
  const config = map[status] || { variant: 'secondary', label: status }
  return <Badge variant={config.variant as 'default'}>{config.label}</Badge>
}

function riskBadge(risk: string) {
  switch (risk) {
    case 'critical': return <Badge variant="destructive">Critical</Badge>
    case 'high': return <Badge className="bg-orange-500/10 text-orange-500 border-orange-500/20">High</Badge>
    case 'medium': return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Medium</Badge>
    case 'low': return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Low</Badge>
    default: return <Badge variant="secondary">{risk}</Badge>
  }
}

function CreateChangeDialog() {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState('normal')
  const [risk, setRisk] = useState('medium')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: { title: string; description: string; type: string; risk: string }) =>
      apiClient.post('/changes', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['changes'] })
      toast({ title: 'RFC created', description: 'Change request created successfully' })
      setOpen(false)
      setTitle('')
      setDescription('')
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create change request', variant: 'destructive' })
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          New RFC
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Change Request</DialogTitle>
          <DialogDescription>Submit a new RFC for review and approval.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Upgrade firewall firmware" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <textarea
              className="w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detailed description of the change..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Risk</Label>
              <Select value={risk} onValueChange={setRisk}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={() => createMutation.mutate({ title, description, type, risk })}
            disabled={!title || !description || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create RFC'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function ChangeManagementPage() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data, isLoading } = useQuery({
    queryKey: ['changes', statusFilter],
    queryFn: () => apiClient.get<{ changes: ChangeRequest[]; pagination: unknown }>('/changes', {
      ...(statusFilter !== 'all' && { status: statusFilter }),
    }),
  })

  const actionMutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: string }) =>
      apiClient.post(`/changes/${id}/${action}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['changes'] })
      toast({ title: 'Updated', description: 'Change request updated' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/changes/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['changes'] })
      toast({ title: 'Deleted', description: 'Change request deleted' })
    },
  })

  const changes = data?.data?.changes || []
  const filtered = changes.filter((c) =>
    c.title.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Change Management</h1>
          <p className="text-muted-foreground">RFC workflow, approvals, and change calendar</p>
        </div>
        <CreateChangeDialog />
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search changes..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="pending_approval">Pending Approval</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="rolled_back">Rolled Back</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ClipboardList className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
            <p className="text-lg font-medium text-muted-foreground">No change requests</p>
            <p className="text-sm text-muted-foreground">Create an RFC to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((change) => (
            <Card key={change.id}>
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">{change.title}</span>
                    {statusBadge(change.status)}
                    {riskBadge(change.risk)}
                    <Badge variant="outline" className="text-xs">{change.type}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-1">{change.description}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(change.createdAt).toLocaleDateString()}
                    </span>
                    {change.scheduledStart && (
                      <span>Scheduled: {new Date(change.scheduledStart).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {change.status === 'draft' && (
                      <DropdownMenuItem onClick={() => actionMutation.mutate({ id: change.id, action: 'submit' })}>
                        <ClipboardList className="mr-2 h-4 w-4" /> Submit for Approval
                      </DropdownMenuItem>
                    )}
                    {(change.status === 'submitted' || change.status === 'pending_approval') && (
                      <>
                        <DropdownMenuItem onClick={() => actionMutation.mutate({ id: change.id, action: 'approve' })}>
                          <CheckCircle className="mr-2 h-4 w-4" /> Approve
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => actionMutation.mutate({ id: change.id, action: 'reject' })}>
                          <XCircle className="mr-2 h-4 w-4" /> Reject
                        </DropdownMenuItem>
                      </>
                    )}
                    {change.status === 'approved' && (
                      <DropdownMenuItem onClick={() => actionMutation.mutate({ id: change.id, action: 'start' })}>
                        <Play className="mr-2 h-4 w-4" /> Start Implementation
                      </DropdownMenuItem>
                    )}
                    {change.status === 'in_progress' && (
                      <>
                        <DropdownMenuItem onClick={() => actionMutation.mutate({ id: change.id, action: 'complete' })}>
                          <CheckCircle className="mr-2 h-4 w-4" /> Complete
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => actionMutation.mutate({ id: change.id, action: 'rollback' })}>
                          <RotateCcw className="mr-2 h-4 w-4" /> Rollback
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-destructive" onClick={() => deleteMutation.mutate(change.id)}>
                      <Trash2 className="mr-2 h-4 w-4" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
